// Iter 579 — Monitoring Pesanan Batal Shopee (read-only) untuk Pantau Produksi Rido.
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getChannel } from "./channel";
import { AlertTriangle, ChevronDown, ChevronRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { k: "cancelled", label: "Dibatalkan" },
  { k: "in_cancel", label: "Pengajuan Pembatalan" },
  { k: "return", label: "Retur / Pengembalian" },
];
const NA = "Informasi tidak tersedia";
const mp = () => (getChannel() === "tokopedia" ? "Tokopedia" : "Shopee");

const fmtWib = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d) || d.getFullYear() < 2015) return "";
  return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" }) + " WIB";
};

function CancelTime({ r, tab }) {
  if (tab !== "cancelled") return <span>Update terakhir {mp()}: {fmtWib(r.last_update_at) || NA}</span>;
  if (r.cancelled_first_seen_at && r.cancel_detection === "transition")
    return <span>Terdeteksi batal: {fmtWib(r.cancelled_first_seen_at)} <em>(waktu batal resmi tidak tersedia dari {mp()})</em></span>;
  if (r.cancelled_first_seen_at)
    return <span>Sudah batal saat pertama tersinkron ({fmtWib(r.cancelled_first_seen_at)}) · waktu batal sebenarnya tidak diketahui</span>;
  return <span>Waktu batal tidak tercatat · update terakhir {mp()} {fmtWib(r.last_update_at) || "—"}</span>;
}

function JobLine({ j }) {
  const divs = (j.divisions || []).map(d => `${d.division}: ${d.pics.length ? d.pics.join(", ") : "Belum Ditugaskan"}`).join(" · ");
  return (
    <li data-testid={`cancel-job-${j.job_id}`}>
      <b>{j.stage_detail}</b>{j.internal_status === "BATAL" && <span className="pmc-done"> · sudah dibatalkan internal</span>}
      <div className="pmc-muted">Divisi & PIC: {divs || (j.pic ? j.pic : "—")}</div>
    </li>
  );
}

function History({ r }) {
  return (
    <div className="pmc-history" data-testid={`cancel-history-${r.order_number}`}>
      <h5>Riwayat status {mp()}</h5>
      {r.status_history.length === 0 ? <p className="pmc-muted">Belum ada perubahan status tercatat (pencatatan dimulai Iter 579).</p> :
        <ol>{r.status_history.map((h, i) => <li key={i}>{h.from || "—"} → <b>{h.to}</b> · update {mp()} {fmtWib(h.shopee_update_time) || "—"} · terdeteksi {fmtWib(h.detected_at)}</li>)}</ol>}
      {r.jobs.map(j => (j.timeline || []).length > 0 && (
        <div key={j.job_id}><h5>Pekerjaan sebelum pembatalan</h5>
          <ol>{j.timeline.map((t, i) => <li key={i}>{t.label} · {t.pic || "—"} · {fmtWib(t.at) || "—"}</li>)}</ol></div>))}
    </div>
  );
}

function CancelRow({ r, tab }) {
  const [open, setOpen] = useState(false);
  const reason = [r.cancel_reason, r.buyer_cancel_reason].filter(Boolean).join(" · ");
  return (
    <article className={`pmc-row ${r.needs_action ? "pmc-row-action" : ""}`} data-testid={`cancel-row-${r.order_number}`}>
      {r.needs_action && <div className="pmc-alert" data-testid={`cancel-needs-action-${r.order_number}`}>
        <AlertTriangle size={14}/> Pesanan dibatalkan di {mp()} — Perlu Tindakan</div>}
      <div className="pmc-head">
        <strong data-testid={`cancel-order-${r.order_number}`}>{r.order_number}</strong>
        <span>{r.buyer || "—"}</span>
        <span className="pmc-muted">{r.shopee_status}</span>
        {!r.entered_production && <span className="pmc-tag">Belum masuk produksi</span>}
      </div>
      <p className="pmc-prod">{(r.products || []).map(p => `${p.name}${p.variation ? ` (${p.variation})` : ""} ×${p.quantity || 0}`).join(" · ") || "—"} · <b>{r.pcs} pcs</b></p>
      <div className="pmc-muted"><CancelTime r={r} tab={tab}/></div>
      <div className="pmc-muted">Alasan: {reason || NA} · Dibatalkan oleh: {r.cancel_by || NA}</div>
      <ul className="pmc-jobs">{r.jobs.map(j => <JobLine key={j.job_id} j={j}/>)}</ul>
      <button type="button" className="pmc-link" onClick={() => setOpen(!open)} data-testid={`cancel-toggle-${r.order_number}`}>
        {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>} Riwayat</button>
      {open && <History r={r}/>}
    </article>
  );
}

export function ShopeeCancelPanel({ onClose }) {
  const [tab, setTab] = useState("cancelled");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/production/monitoring/shopee-cancellations`, { params: { tab, q }, withCredentials: true });
      setData(r.data); setErr("");
    } catch (e) { setErr(`Gagal memuat pesanan batal${e.response?.status ? ` (kode ${e.response.status})` : ""}.`); }
  }, [tab, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <section className="pmc" data-testid="shopee-cancel-panel">
      <header className="pmc-top">
        <h3>Pesanan Batal {getChannel() === "tokopedia" ? "Tokopedia" : "Shopee"}</h3>
        {getChannel() === "tokopedia" && <span className="pmc-muted" data-testid="cancel-panel-manual-note">Data Tokopedia = status yang dicatat Admin di tab Tokopedia, bukan data resmi API.</span>}
        <span className="pmc-muted">Satuan: nomor pesanan unik · hanya yang punya pekerjaan TAMFARIS · 30 hari terakhir (pekerjaan yang belum ditindaklanjuti selalu tampil). Tidak mengubah pekerjaan.</span>
        <button type="button" onClick={onClose} className="pmc-close" data-testid="cancel-panel-close"><X size={16}/> Tutup</button>
      </header>
      <nav className="pmc-tabs">
        {TABS.map(t => <button key={t.k} type="button" className={tab === t.k ? "active" : ""} onClick={() => setTab(t.k)}
          data-testid={`cancel-tab-${t.k}`}>{t.label} <span>{data?.counts?.[t.k] ?? "…"}</span></button>)}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cari nomor pesanan / pelanggan" data-testid="cancel-search"/>
      </nav>
      {err && <div className="monitor-v2-error" role="alert" data-testid="cancel-error">{err}
        <button type="button" onClick={load} data-testid="cancel-retry">Coba Lagi</button></div>}
      {data && data.rows.length === 0 && <p className="pmc-muted" data-testid="cancel-empty">Tidak ada pesanan pada tab ini.</p>}
      <div className="pmc-list">{(data?.rows || []).map(r => <CancelRow key={r.order_number} r={r} tab={tab}/>)}</div>
    </section>
  );
}
